import { useState } from 'react'
import type { CountryCode } from '../../config/locales'
import type { LineGenerationConfig, TdLine } from '../../types/testdata.types'
import {
  LS_COMPACT_PDF_LINES, LS_INCLUDE_GROUND_TRUTH, LS_INCLUDE_LOCALE_RECOGNITION_TEXT,
  LS_LINE_GENERATION, LS_ROUND_UNIT_PRICE, LS_ROUND_UNIT_PRICE_VALUE,
  defaultLineGenerationConfig, emptyLine,
} from './constants'

function loadLineGenerationConfig(): LineGenerationConfig {
  try {
    const raw = localStorage.getItem(LS_LINE_GENERATION)
    if (raw) return { ...defaultLineGenerationConfig(), ...JSON.parse(raw) }
  } catch { /* corrupt */ }
  return defaultLineGenerationConfig()
}

/** Artikelregels + de twee weergave-opties die op elke regel inwerken:
 * ronde stuksprijzen (voorkomt BTW-afrondingsverschillen bij testen met veel
 * regels) en compacte PDF-regels (geen tekstwrap, alles op één regel). */
export function useLinesState(country: CountryCode) {
  const [lines, setLines] = useState<TdLine[]>([emptyLine()])
  // Ronde stukprijzen: forceert alle regels op een vaste, ronde stuksprijs (bv. €1) —
  // voorkomt afrondingsverschillen tussen regel-BTW en documenttotaal-BTW bij het
  // testen met veel regels, zonder dat de gebruiker zich zorgen hoeft te maken over
  // afrondingsverschillen door willekeurige/fractionele prijzen.
  const [roundUnitPrices, setRoundUnitPricesRaw] = useState(
    () => localStorage.getItem(LS_ROUND_UNIT_PRICE) === 'true'
  )
  const [roundUnitPriceValue, setRoundUnitPriceValueRaw] = useState(
    () => Number(localStorage.getItem(LS_ROUND_UNIT_PRICE_VALUE) ?? '1') || 1
  )
  // Compacte PDF-regels: dwingt elke artikelregel af op precies één PDF-regel (lange
  // artikel-/omschrijvingsteksten worden afgekapt i.p.v. te wrappen) — voorkomt dat de
  // PDF onnodig lang wordt bij veel/lange regels. Heeft geen effect op de XML.
  const [compactPdfLines, setCompactPdfLinesRaw] = useState(
    () => localStorage.getItem(LS_COMPACT_PDF_LINES) === 'true'
  )
  // CAP-013 / REQ-TESTREF v0.2: optioneel blok met locale-specifieke getallen/
  // datums/tijden/tekens op de primaire invoice-PDF (default false, ongewijzigd
  // gedrag voor bestaande flows). Geen blijvende voorkeur nodig, maar bewust wel
  // persistent net als compactPdfLines — dit is een generatie-instelling, geen
  // e-mail-specifieke keuze.
  const [includeLocaleRecognitionText, setIncludeLocaleRecognitionTextRaw] = useState(
    () => localStorage.getItem(LS_INCLUDE_LOCALE_RECOGNITION_TEXT) === 'true'
  )
  // CAP-013 / REQ-TESTREF-010: centrale Ground Truth-keuze — één instelling
  // voor zowel downloaden (aparte *_GROUND-TRUTH.pdf naast de primaire ZIP-
  // inhoud) als mailen (aparte PDF-bijlage). Vervangt de vroegere merged
  // Human Review PDF. Alleen relevant voor invoice + niet-Factur-X — de UI
  // ontkoppelt/verbergt de toggle zelf in andere combinaties i.p.v. een
  // verwarrende backend-422 te tonen.
  const [includeGroundTruth, setIncludeGroundTruthRaw] = useState(
    () => localStorage.getItem(LS_INCLUDE_GROUND_TRUTH) === 'true'
  )
  // REQ-TESTDATA-003 (v0.8): optional opt-in precision-test line generation.
  // Default `mode: 'catalogue'` — persisted, but stays a complete no-op on the
  // request body until the user explicitly switches to 'precision_test'.
  const [lineGeneration, setLineGenerationRaw] = useState<LineGenerationConfig>(loadLineGenerationConfig)
  // REQ-TESTDATA-004 (v0.8.1) — whether `lines` currently IS the canonical
  // precision-test preview for the CURRENT `lineGeneration`/price-list
  // selection (set true only after a successful precision materialization
  // via `/random-lines`). Any settings change resets this to false so a
  // stale preview can never be silently submitted as though it matched the
  // now-current config — the user must explicitly regenerate. Manual edits
  // to an already-materialized line do NOT reset this: an edited line stays
  // part of the materialized set, just user-overridden (REQ-TESTDATA-004 §6).
  const [precisionMaterialized, setPrecisionMaterializedRaw] = useState(false)

  const updateLine = (idx: number, key: keyof TdLine, val: string | number) =>
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, [key]: val } : l))

  // REQ-TESTDATA-004 (v0.8.1) — used by the decimal-literal-preserving quantity/
  // unit-price inputs on a materialized precision row: updates the numeric
  // value AND its intended scale together, derived from what the user typed
  // (e.g. typing "7,125" sets value=7.125, scale=3) rather than losing the
  // literal's trailing zeros/precision the way a plain `<input type="number">`
  // would. Never used for ordinary catalogue-mode cells (those keep calling
  // `updateLine` exactly as before via a plain number input).
  const updateLineDecimal = (idx: number, field: 'quantity' | 'unit_price', value: number, scale: number) => {
    const scaleKey = field === 'quantity' ? 'quantity_scale' : 'unit_price_scale'
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, [field]: value, [scaleKey]: scale } : l))
  }

  const addLine = () => setLines(ls => [...ls, { ...emptyLine(country), unit_price: roundUnitPrices ? roundUnitPriceValue : 0 }])
  const removeLine = (idx: number) => setLines(ls => ls.filter((_, i) => i !== idx))

  const setRoundUnitPrices = (checked: boolean) => {
    setRoundUnitPricesRaw(checked)
    localStorage.setItem(LS_ROUND_UNIT_PRICE, String(checked))
    if (checked) setLines(ls => ls.map(l => ({ ...l, unit_price: roundUnitPriceValue })))
  }

  const setRoundUnitPriceValue = (value: number) => {
    const safeValue = value > 0 ? value : 1
    setRoundUnitPriceValueRaw(safeValue)
    localStorage.setItem(LS_ROUND_UNIT_PRICE_VALUE, String(safeValue))
    if (roundUnitPrices) setLines(ls => ls.map(l => ({ ...l, unit_price: safeValue })))
  }

  const setCompactPdfLines = (checked: boolean) => {
    setCompactPdfLinesRaw(checked)
    localStorage.setItem(LS_COMPACT_PDF_LINES, String(checked))
  }

  const setIncludeLocaleRecognitionText = (checked: boolean) => {
    setIncludeLocaleRecognitionTextRaw(checked)
    localStorage.setItem(LS_INCLUDE_LOCALE_RECOGNITION_TEXT, String(checked))
  }

  const setIncludeGroundTruth = (checked: boolean) => {
    setIncludeGroundTruthRaw(checked)
    localStorage.setItem(LS_INCLUDE_GROUND_TRUTH, String(checked))
  }

  const setLineGeneration = (update: Partial<LineGenerationConfig>) => {
    setLineGenerationRaw(prev => {
      const next = { ...prev, ...update }
      localStorage.setItem(LS_LINE_GENERATION, JSON.stringify(next))
      return next
    })
    // REQ-TESTDATA-004 §5/§8: any settings change (line count, quantity mode,
    // precision ranges, vary toggle, or turning the mode itself on) makes the
    // previous preview stale — never silently reinterpreted as matching the
    // new config.
    setPrecisionMaterializedRaw(false)
  }

  const setPrecisionMaterialized = (value: boolean) => setPrecisionMaterializedRaw(value)

  return {
    lines, setLines,
    updateLine, updateLineDecimal, addLine, removeLine,
    roundUnitPrices, setRoundUnitPrices,
    roundUnitPriceValue, setRoundUnitPriceValue,
    compactPdfLines, setCompactPdfLines,
    includeLocaleRecognitionText, setIncludeLocaleRecognitionText,
    includeGroundTruth, setIncludeGroundTruth,
    lineGeneration, setLineGeneration,
    precisionMaterialized, setPrecisionMaterialized,
  }
}

export type LinesState = ReturnType<typeof useLinesState>
